import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { blogPosts } from "@/data/blog";
import CTASection from "@/components/sections/CTASection";

export default function Blog() {
  const [hero, ...rest] = blogPosts;
  return (
    <>
      <SEO
        title="Blog — Articles & ressources sur l'IA"
        description="Articles SEO sur l'intelligence artificielle : ChatGPT, outils IA 2025, agents IA, IA générative. Ressources pédagogiques par les formateurs YAFORMIS."
        path="/blog"
      />
      <section className="bg-brand-page py-12 md:py-16 border-b border-brand-line" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="overline">Blog YAFORMIS</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] text-brand-ink">
            Apprendre l'IA, <span className="text-brand-blue">étape par étape.</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-3xl leading-relaxed">
            Articles pédagogiques rédigés par nos formateurs : fondamentaux, outils, bonnes pratiques, retours terrain.
          </p>
        </div>
      </section>

      {/* Featured */}
      <section className="bg-brand-surface py-12 md:py-16" data-testid="blog-featured-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Link
            to={`/blog/${hero.slug}`}
            className="group block grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 bg-white border border-brand-line hover:border-brand-ink transition-colors"
            data-testid="blog-featured"
          >
            <div className="lg:col-span-7 aspect-[16/10] lg:aspect-auto overflow-hidden bg-[#0a1830]">
              <img src={hero.cover} alt={hero.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="lg:col-span-5 p-7 md:p-10 flex flex-col justify-center">
              <div className="overline">À la une · {hero.category}</div>
              <h2 className="font-heading font-black text-3xl md:text-4xl mt-4 leading-tight text-brand-ink group-hover:text-brand-blue transition-colors">
                {hero.title}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">{hero.excerpt}</p>
              <div className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand-ink">
                Lire l'article <ArrowUpRight className="w-4 h-4" />
              </div>
              <div className="mt-4 text-xs text-slate-500">
                {hero.author} · {hero.readingTime} · {new Date(hero.publishedAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-brand-page py-12 md:py-16" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-white border border-brand-line hover:border-brand-ink transition-colors flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#0a1830]">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-semibold text-brand-blue uppercase tracking-wider">{p.category}</span>
                    <span>·</span>
                    <span>{p.readingTime}</span>
                  </div>
                  <h3 className="font-heading font-bold text-xl mt-3 leading-snug text-brand-ink group-hover:text-brand-blue transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-ink group-hover:text-brand-blue">
                    Lire l'article <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
